import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { getDevices, resetSensorErrorCounts } from '../../stores/actions/installerActions';
import ResetSensorErrorCountsConfirmation from '../../components/ResetSensorErrorCounts/ResetSensorErrorCountsConfirmation';
import Snackbar from '../../../components/Snackbar/Snackbar';
import { DataTable } from 'primereact/datatable';
import { DataView } from 'primereact/dataview';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { exportDataTableCsv } from '../../helpers/exportCsv';
import { FilterMatchMode, FilterService } from 'primereact/api';
import { Link } from 'react-router-dom';
import Info from '@material-ui/icons/Info';

const getRangefinderStatusInfo = (value: number) => {
  if (value === 0) return { label: '0', severity: 'success' as const, description: 'Sensor operating normally' };
  if (value === -1) return { label: '-1', severity: 'warning' as const, description: 'Unable to measure' };
  return { label: String(value), severity: 'danger' as const, description: 'Error' };
};

const renderSensorStatusTag = (value: number | null | undefined) => {
  if (value === null || value === undefined) return null;
  const info = getRangefinderStatusInfo(value);
  return <Tag value={info.label} severity={info.severity} title={info.description} />;
};

type SupplyState = 'battery_good_normal' | 'battery_weak' | 'battery_empty';

const LPT_DATE_RANGE_MATCH_MODE = 'lptDateRange';
FilterService.register(LPT_DATE_RANGE_MATCH_MODE, (value: Date | null, range: (Date | null)[] | null) => {
  if (!Array.isArray(range) || (!range[0] && !range[1])) return true;
  if (!value) return false;
  const [from, to] = range;
  if (from) {
    const fromStart = new Date(from);
    fromStart.setHours(0, 0, 0, 0);
    if (value < fromStart) return false;
  }
  if (to) {
    const toEnd = new Date(to);
    toEnd.setHours(23, 59, 59, 999);
    if (value > toEnd) return false;
  }
  return true;
});

const lptFilterElement = (options: any) => (
  <Calendar
    value={options.value}
    onChange={(e) => options.filterApplyCallback(e.value)}
    selectionMode="range"
    readOnlyInput
    dateFormat="yy-mm-dd"
    placeholder="From - To"
    showButtonBar
    className="w-full"
  />
);

const renderLptTag = (lpt: Date | null) => {
    if (!lpt) return null;
    const formatted = lpt.toLocaleString('sv-SE').replace('T', ' ');
    const now = Date.now();
    const hoursAgo = (now - lpt.getTime()) / (1000 * 60 * 60);

    if (hoursAgo > 72) return <Tag value={formatted} severity="danger" />;
    if (hoursAgo > 24) return <Tag value={formatted} severity="warning" />;
    if (hoursAgo > 6) return <Tag value={formatted} style={{ backgroundColor: '#eab308', color: '#000' }} />;
    return formatted;
};

const DebouncedTextFilter = ({ options }: { options: any }) => {
  const [localValue, setLocalValue] = useState<string>(options.value ?? '');
  return (
    <InputText
      value={localValue}
      onChange={(e) => {
        setLocalValue(e.target.value);
        options.filterApplyCallback(e.target.value);
      }}
      className="p-column-filter"
    />
  );
};

const getDeviceTypeGeneration = (deviceType: string): string => {
  if (deviceType.includes('-10.')) return '10';
  if (deviceType.includes('-12.')) return '12';
  if (deviceType.includes('-16.')) return '16';
  if (deviceType.includes('-17.')) return '17';
  if (deviceType.includes('-20.')) return '20';
  if (deviceType.includes('-25.')) return '25';
  return 'other';
};

const renderDeviceTypeTag = (deviceType: string) => <Tag value={deviceType} severity="secondary" />;

const GEN_OPTIONS = [
  { label: '10', value: '10' },
  { label: '12', value: '12' },
  { label: '16', value: '16' },
  { label: '17', value: '17' },
  { label: '20', value: '20' },
  { label: '25', value: '25' },
  { label: 'other', value: 'other' },
];

const DEVICES_COLUMNS_STORAGE_KEY = 'devicesList.visibleColumns';

const loadVisibleColumns = (): ColumnKey[] => {
  try {
    const raw = localStorage.getItem(DEVICES_COLUMNS_STORAGE_KEY);
    if (!raw) return [...ALWAYS_VISIBLE, ...W2_ONLY, ...LoRa_ONLY];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...ALWAYS_VISIBLE, ...W2_ONLY, ...LoRa_ONLY];
    const validKeys = new Set(columnOptionsAll.map((opt) => opt.value));
    const filtered = parsed.filter((v) => validKeys.has(v));
    return filtered.length > 0 ? filtered : [...ALWAYS_VISIBLE, ...W2_ONLY, ...LoRa_ONLY];
  } catch {
    return [...ALWAYS_VISIBLE, ...W2_ONLY, ...LoRa_ONLY];
  }
};

interface DeviceRow {
  id: string;
  systemName: string | null;
  systemID: number | null;
  nodeName: string | null;
  serial: string;
  subSerial: string | null;
  type: string;
  gen: string;
  subType: string | null;
  firmware: string | null;
  lpt: Date | null;
  snr: string | null;
  rssi: string | null;
  region: string | null;
  gatewayID: string | null;
  nodeId: number | null;
  batteries: string | null;
  batteryPercentage: number | null;
  w2ConnectionStatus: number | null;
  rangefinderStatus: number | null;
  sensorErrorCount: number | null;
  sensorErrorCountResetAt: Date | null;
  temperature1: number | null;
  temperatureOk: boolean | null;
  temperature2: number | null;
  supplyVoltage1: number | null;
  supplyVoltage2: number | null;
  systemVersion: string;
  systemVersionCategory: 'W2' | 'LoRa';
  nodeLinked: 'Y' | 'N';
}

type ColumnKey =
  | 'systemName' | 'nodeName' | 'serial' | 'subSerial' | 'type' | 'gen' | 'subType' | 'batteries' | 'batteryPercentage'
  | 'rangefinderStatus' | 'sensorErrorCount' | 'temperature1' | 'temperature2' | 'supplyVoltage1' | 'supplyVoltage2'
  | 'firmware' | 'node' | 'gatewayID' | 'w2ConnectionStatus' | 'lpt' | 'snr' | 'rssi' | 'region';

const ALWAYS_VISIBLE: ColumnKey[] = [
  'systemName', 'nodeName', 'serial', 'subSerial', 'type', 'gen', 'subType', 'batteries', 'batteryPercentage',
  'rangefinderStatus', 'sensorErrorCount', 'temperature1', 'temperature2', 'supplyVoltage1', 'supplyVoltage2',
  'firmware', 'node', 'gatewayID',
];
const W2_ONLY: ColumnKey[] = ['w2ConnectionStatus'];
const LoRa_ONLY: ColumnKey[] = ['lpt', 'snr', 'rssi', 'region'];

const SUPPLY_STATE_SEVERITY: Record<SupplyState, 'success' | 'warning' | 'danger'> = {
  battery_good_normal: 'success',
  battery_weak: 'warning',
  battery_empty: 'danger',
};

// number of times a sensor error appeared since the counter was last reset; onReset - reset button (counter above 0)
const renderSensorErrorCount = (count: number | null, resetAt: Date | null, onReset?: () => void) => {
  if (count == null) return null;
  return (
    <div>
      <div className="flex align-items-center gap-1">
        {count > 0 ? <Tag value={String(count)} severity="danger" /> : '0'}
        {count > 0 && onReset && (
          <Button icon="pi pi-refresh" rounded text size="small" severity="secondary" tooltip="Reset counter"
            tooltipOptions={{ position: 'top' }} aria-label="Reset sensor error counter" onClick={onReset}
            style={{ width: '1.75rem', height: '1.75rem' }} />
        )}
      </div>
      {resetAt && <div className="text-xs text-color-secondary">since {resetAt.toLocaleDateString('sv-SE')}</div>}
    </div>
  );
};

const renderBatteryPercentageTag = (percentage: number | null, supplyState: SupplyState | null) => {
  if (percentage == null) return null;
  if (!supplyState) return `${percentage}%`;
  return <Tag value={`${percentage}%`} severity={SUPPLY_STATE_SEVERITY[supplyState]} />;
};

const renderVersionTag = (systemVersion: string) => (
  <Tag
    value={systemVersion === 'W2' ? 'WSN' : 'LoRa'}
    severity={systemVersion === 'W2' ? 'info' : 'secondary'}
  />
);

// EN91/EN92/EN95 are the backend's internal battery type codes; displayed here under
// their common battery-size names (AA / AAA / D).
export const formatBatteries = (en91: number, en92: number, en95: number): string | null => {
  const parts: string[] = [];
  if (en91 > 0) parts.push(`AAx${en91}`);
  if (en92 > 0) parts.push(`AAAx${en92}`);
  if (en95 > 0) parts.push(`Dx${en95}`);
  return parts.length > 0 ? parts.join(' ') : null;
};

const systemVersionOptions = [
  { label: 'All systems', value: 'all' },
  { label: 'WSN', value: 'W2' },
  { label: 'LoRa', value: 'LoRa' },
];

const columnOptionsAll = [
  { label: 'System', value: 'systemName' },
  { label: 'Node name', value: 'nodeName' },
  { label: 'Device EUI', value: 'serial' },
  { label: 'Sub serial', value: 'subSerial' },
  { label: 'Device type', value: 'type' },
  { label: 'Gen.', value: 'gen' },
  { label: 'Sub type', value: 'subType' },
  { label: 'Batteries', value: 'batteries' },
  { label: 'Battery %', value: 'batteryPercentage' },
  { label: 'Sensor status', value: 'rangefinderStatus' },
  { label: 'Sens. err. count', value: 'sensorErrorCount' },
  { label: 'Temperature', value: 'temperature1' },
  { label: 'Sens. Temp.', value: 'temperature2' },
  { label: 'VCC', value: 'supplyVoltage1' },
  { label: 'VDD', value: 'supplyVoltage2' },
  { label: 'Firmware', value: 'firmware' },
  { label: 'In system', value: 'node' },
  { label: 'Gateway', value: 'gatewayID' },
  { label: 'WSN conn. status', value: 'w2ConnectionStatus' },
  { label: 'Latest packet timestamp', value: 'lpt' },
  { label: 'SNR', value: 'snr' },
  { label: 'RSSI', value: 'rssi' },
  { label: 'Region', value: 'region' },
];

const nodeFilterOptions = [
  { label: 'Y', value: 'Y' },
  { label: 'N', value: 'N' },
];

interface IDevicesListProps {
  mobileView: boolean;
  devices: any[];
  getDevices: () => void;
  resetSensorErrorCounts: (systemId: number | null, nodeId: number | null) => Promise<number | null>;
}

const createFilters = () => ({
  systemName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  nodeName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  serial: { value: null, matchMode: FilterMatchMode.CONTAINS },
  subSerial: { value: null, matchMode: FilterMatchMode.CONTAINS },
  type: { value: null, matchMode: FilterMatchMode.CONTAINS },
  gen: { value: null, matchMode: FilterMatchMode.EQUALS },
  subType: { value: null, matchMode: FilterMatchMode.CONTAINS },
  batteries: { value: null, matchMode: FilterMatchMode.CONTAINS },
  batteryPercentage: { value: null, matchMode: FilterMatchMode.CONTAINS },
  rangefinderStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
  sensorErrorCount: { value: null, matchMode: FilterMatchMode.CONTAINS },
  temperature1: { value: null, matchMode: FilterMatchMode.CONTAINS },
  temperature2: { value: null, matchMode: FilterMatchMode.CONTAINS },
  supplyVoltage1: { value: null, matchMode: FilterMatchMode.CONTAINS },
  supplyVoltage2: { value: null, matchMode: FilterMatchMode.CONTAINS },
  firmware: { value: null, matchMode: FilterMatchMode.CONTAINS },
  nodeLinked: { value: null, matchMode: FilterMatchMode.EQUALS },
  gatewayID: { value: null, matchMode: FilterMatchMode.CONTAINS },
  w2ConnectionStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
  lpt: { value: null, matchMode: LPT_DATE_RANGE_MATCH_MODE },
  snr: { value: null, matchMode: FilterMatchMode.CONTAINS },
  rssi: { value: null, matchMode: FilterMatchMode.CONTAINS },
  region: { value: null, matchMode: FilterMatchMode.CONTAINS },
  systemVersionCategory: { value: null, matchMode: FilterMatchMode.EQUALS },
});

const DevicesList = ({ mobileView, devices, getDevices, resetSensorErrorCounts }: IDevicesListProps) => {
  useEffect(() => {
    getDevices();
  }, [getDevices]);

  // sensor error counter reset of a single device (its node)
  const [resetRow, setResetRow] = useState<DeviceRow | null>(null);
  const [resetting, setResetting] = useState(false);
  const [notification, setNotification] = useState<{ message: string; color: 'success' | 'danger' } | null>(null);

  useEffect(() => {
    if (!notification) return;
    const timeout = setTimeout(() => setNotification(null), 6000);
    return () => clearTimeout(timeout);
  }, [notification]);

  const resetSensorErrorCount = () => {
    if (!resetRow?.nodeId) return;
    setResetting(true);
    resetSensorErrorCounts(null, resetRow.nodeId).then((count) => {
      setResetting(false);
      if (count === null) {
        setNotification({ message: 'Resetting sensor error counter failed', color: 'danger' });
      } else {
        setResetRow(null);
        setNotification({ message: 'Sensor error counter reset', color: 'success' });
        getDevices();
      }
    });
  };

  const sensorErrorCountCell = (row: DeviceRow) =>
    renderSensorErrorCount(row.sensorErrorCount, row.sensorErrorCountResetAt, row.nodeId ? () => setResetRow(row) : undefined);

  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(loadVisibleColumns);
  const [filters, setFilters] = useState(createFilters());
  const dt = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(DEVICES_COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns));
    } catch {
    }
  }, [visibleColumns]);

  // mobile-specific state
  const [mobileFilters, setMobileFilters] = useState({
    systemName: '', nodeName: '', serial: '', type: '', gen: '', firmware: '', gatewayID: '', systemVersionCategory: 'all' as 'all' | 'W2' | 'LoRa',
  });
  const [pendingFilters, setPendingFilters] = useState(mobileFilters);
  const [mobileSortField, setMobileSortField] = useState('default');
  const [mobileSortOrder, setMobileSortOrder] = useState<-1 | 1>(1);
  const [pendingSortField, setPendingSortField] = useState(mobileSortField);
  const [pendingSortOrder, setPendingSortOrder] = useState(mobileSortOrder);
  const [mobileFilterDialog, setMobileFilterDialog] = useState(false);

  const rows: DeviceRow[] = useMemo(() => {
    return (Array.isArray(devices) ? devices : []).map((device) => {
      let snr, rssi, region, gatewayId = null;
      if (device.SignalQualityInfo) {
        const parsed = JSON.parse(device.SignalQualityInfo);
        snr = parsed[0]?.snr?.toFixed(1);
        rssi = parsed[0]?.rssi?.toFixed(1);
        region = parsed[0]?.metadata?.region_common_name;
        gatewayId = parsed[0]?.gatewayId?.toUpperCase();
      }

      return {
        id: device.ID,
        systemName: device.SystemName,
        systemID: device.SystemID,
        nodeName: device.NodeName,
        serial: device.SerialString,
        subSerial: device.SubSerialString,
        type: device.DeviceType,
        gen: getDeviceTypeGeneration(device.DeviceType),
        subType: device.SubType,
        firmware: device.FirmwareVersion,
        lpt: device.LatestPacketTimestamp ? new Date(device.LatestPacketTimestamp) : null,
        snr,
        rssi,
        region,
        gatewayID: device.SystemVersion === 'W2' ? (device.w2GatewaySerial ?? null) : gatewayId,
        nodeId: device.NodeId,
        nodeLinked: device.NodeId ? 'Y' : 'N',
        batteries: formatBatteries(device.en91Count, device.en92Count, device.en95Count),
        batteryPercentage: device.BatteryPercentage,
        w2ConnectionStatus: device.W2ConnectionStatus,
        rangefinderStatus: device.RangefinderStatus,
        sensorErrorCount: device.sensorErrorCount,
        sensorErrorCountResetAt: device.sensorErrorCountResetAt ? new Date(device.sensorErrorCountResetAt) : null,
        temperature1: device.Temperature1,
        temperatureOk: device.TemperatureOK,
        temperature2: device.Temperature2,
        supplyVoltage1: device.SupplyVoltage1,
        supplyVoltage2: device.SupplyVoltage2,
        systemVersion: device.SystemVersion,
        systemVersionCategory: device.SystemVersion === 'W2' ? 'W2' : 'LoRa',
        supplyState: device.SupplyState,
      };
    });
  }, [devices]);

const mobileFilteredRows = useMemo(() => {
    let result = [...rows];
    if (mobileFilters.systemVersionCategory !== 'all')
      result = result.filter(r => r.systemVersionCategory === mobileFilters.systemVersionCategory);
    if (mobileFilters.systemName)
      result = result.filter(r => r.systemName?.toLowerCase().includes(mobileFilters.systemName.toLowerCase()));
    if (mobileFilters.nodeName)
      result = result.filter(r => r.nodeName?.toLowerCase().includes(mobileFilters.nodeName.toLowerCase()));
    if (mobileFilters.serial)
      result = result.filter(r => r.serial?.toLowerCase().includes(mobileFilters.serial.toLowerCase()));
    if (mobileFilters.type)
      result = result.filter(r => r.type?.toLowerCase().includes(mobileFilters.type.toLowerCase()));
    if (mobileFilters.gen)
      result = result.filter(r => r.gen === mobileFilters.gen);
    if (mobileFilters.firmware)
      result = result.filter(r => r.firmware?.toLowerCase().includes(mobileFilters.firmware.toLowerCase()));
    if (mobileFilters.gatewayID)
      result = result.filter(r => r.gatewayID?.toLowerCase().includes(mobileFilters.gatewayID.toLowerCase()));

    // 'default' preserves the API's own order (latest activity first, per the SQL ORDER BY)
    if (mobileSortField === 'default') {
      return result;
    }

    if (mobileSortField !== 'default') {
      result.sort((a, b) => {
        const aVal = a[mobileSortField];
        const bVal = b[mobileSortField];

        const aEmpty = aVal === null || aVal === undefined || aVal === '';
        const bEmpty = bVal === null || bVal === undefined || bVal === '';

        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;

        if (aVal < bVal) return -1 * mobileSortOrder;
        if (aVal > bVal) return 1 * mobileSortOrder;
        return 0;
      });
    }
    return result;
  }, [rows, mobileFilters, mobileSortField, mobileSortOrder]);

    const isVisible = (key: ColumnKey) => {
        const category = filters.systemVersionCategory?.value;
        if (category === 'W2' && LoRa_ONLY.includes(key)) return false;
        if (category === 'LoRa' && W2_ONLY.includes(key)) return false;
        return visibleColumns.includes(key);
    };

    const columnOptions = useMemo(() => {
        const category = filters.systemVersionCategory?.value;
        return columnOptionsAll.filter(opt => {
        if (category === 'W2' && LoRa_ONLY.includes(opt.value as ColumnKey)) return false;
        if (category === 'LoRa' && W2_ONLY.includes(opt.value as ColumnKey)) return false;
        return true;
        });
    }, [filters.systemVersionCategory?.value]);

  const nodeFilterElement = (options: any) => (
    <Dropdown
      value={options.value}
      options={nodeFilterOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="Y/N"
      showClear
      className="w-full"
    />
  );

const systemVersionFilterOptions = [
  { label: 'WSN', value: 'W2' },
  { label: 'LoRa', value: 'LoRa' },
];

const systemVersionFilterElement = (options: any) => (
  <Dropdown
    value={options.value}
    options={systemVersionFilterOptions}
    onChange={(e) => options.filterApplyCallback(e.value)}
    placeholder="Any"
    showClear
    className="w-full"
  />
 );

  const openMobileFilterDialog = () => {
    setPendingFilters(mobileFilters);
    setPendingSortField(mobileSortField);
    setPendingSortOrder(mobileSortOrder);
    setMobileFilterDialog(true);
  };

  const applyMobileFilters = () => {
    setMobileFilters(pendingFilters);
    setMobileSortField(pendingSortField);
    setMobileSortOrder(pendingSortOrder);
    setMobileFilterDialog(false);
  };

  const resetMobileFilters = () => {
    const empty = { systemName: '', nodeName: '', serial: '', type: '', gen: '', firmware: '', gatewayID: '', systemVersionCategory: 'all' as const };
    setPendingFilters(empty);
    setPendingSortField('default');
    setPendingSortOrder(1);
  };

  const activeFilterCount = useMemo(() => {
    return Object.values(mobileFilters).filter(v => v !== '' && v !== null).length;
  }, [mobileFilters]);

  const sortOptions = [
    { label: 'Default (latest activity)', value: 'default' },
    { label: 'System', value: 'systemName' },
    { label: 'Node', value: 'nodeName' },
    { label: 'Device EUI', value: 'serial' },
    { label: 'Device type', value: 'type' },
    { label: 'Gen.', value: 'gen' },
    { label: 'Firmware', value: 'firmware' },
  ];

  const cardTemplate = (row: DeviceRow) => (
    <div className="col-12">
      <div className="border-1 surface-border border-round p-3 mb-2 surface-card">
        <div className="flex justify-content-between align-items-center pb-2 mb-3 border-bottom-1 surface-border">
          <span className="font-bold text-lg">{row.serial}</span>
          <div className="flex align-items-center gap-2">
            {renderVersionTag(row.systemVersion)}
            <Link to={`/devices/${row.id}/details`}>
              <Button icon="pi pi-eye" className="p-button-rounded p-button-outlined" aria-label="View device details" />
            </Link>
          </div>
        </div>

        <div className="mb-2 p-2 surface-50 border-round">
          <div className="grid">
            <div className="col-6">
              <div className="text-xs text-color-secondary">System</div>
              <div className="font-medium">{row.systemName || '---'}</div>
            </div>
            <div className="col-6">
              <div className="text-xs text-color-secondary">Node</div>
              <div className="font-medium">{row.nodeName || '---'}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col-12">
              <div className="text-xs text-color-secondary">Sub serial</div>
              <div className="font-medium">{row.subSerial || '---'}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col-4">
              <div className="text-xs text-color-secondary">Type</div>
              <div className="font-medium">{row.type}</div>
            </div>
            <div className="col-4">
              <div className="text-xs text-color-secondary">Gen.</div>
              <div className="font-medium">{row.gen}</div>
            </div>
            <div className="col-4">
              <div className="text-xs text-color-secondary">Sub type</div>
              <div className="font-medium">{row.subType || '---'}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col-12">
              <div className="text-xs text-color-secondary">Firmware</div>
              <div className="font-medium">{row.firmware || '---'}</div>
            </div>
          </div>
        </div>

        <div className="mb-2 p-2 surface-50 border-round">
          <div className="grid">
            <div className="col-6">
              <div className="text-xs text-color-secondary">Batteries</div>
              <div className="font-medium">{row.batteries || '---'}</div>
            </div>
            <div className="col-6">
              <div className="text-xs text-color-secondary">Battery %</div>
              <div className="font-medium">{renderBatteryPercentageTag(row.batteryPercentage, row.supplyState) || '---'}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col-6">
              <div className="text-xs text-color-secondary">Sensor status</div>
              <div className="font-medium">{renderSensorStatusTag(row.rangefinderStatus) || '---'}</div>
            </div>
            <div className="col-6">
              <div className="text-xs text-color-secondary">Sens. err. count</div>
              <div className="font-medium">{sensorErrorCountCell(row) ?? '---'}</div>
            </div>
            <div className="col-6">
              <div className="text-xs text-color-secondary">In system</div>
              <div className="font-medium">{row.nodeLinked}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col-6">
              <div className="text-xs text-color-secondary">Temperature</div>
              <div
                className="font-medium inline-block px-1"
                style={{ backgroundColor: row.temperatureOk === false ? '#fddede' : 'transparent' }}
              >
                {row.temperature1 != null ? `${row.temperature1}°C` : '---'}
              </div>
            </div>
            <div className="col-6">
              <div className="text-xs text-color-secondary">Sens. Temp.</div>
              <div className="font-medium">{row.temperature2 != null ? `${row.temperature2}°C` : '---'}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col-6">
              <div className="text-xs text-color-secondary">VCC / VDD</div>
              <div className="font-medium">
                {row.supplyVoltage1 != null ? `${row.supplyVoltage1}V` : '---'} / {row.supplyVoltage2 != null ? `${row.supplyVoltage2}V` : '---'}
              </div>
            </div>
            <div className="col-6">
              <div className="text-xs text-color-secondary">Gateway</div>
              <div className="font-medium">{row.gatewayID || '---'}</div>
            </div>
          </div>
          {row.systemVersion === 'W2' && (
            <div className="grid">
              <div className="col-6">
                <div className="text-xs text-color-secondary">WSN conn. status</div>
                <div className="font-medium">{row.w2ConnectionStatus ?? '---'}</div>
              </div>
            </div>
          )}
          {row.systemVersion !== 'W2' && (
            <>
              <div className="grid">
                <div className="col-6">
                  <div className="text-xs text-color-secondary">Latest packet</div>
                  <div className="font-medium">{row.lpt ? row.lpt.toLocaleString('sv-SE').replace('T', ' ') : '---'}</div>
                </div>
                <div className="col-6">
                  <div className="text-xs text-color-secondary">SNR / RSSI</div>
                  <div className="font-medium">{row.snr ?? '---'} / {row.rssi ?? '---'}</div>
                </div>
              </div>
              <div className="grid">
                <div className="col-6">
                  <div className="text-xs text-color-secondary">Region</div>
                  <div className="font-medium">{row.region || '---'}</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const mobileFilterBar = (
    <div className="flex align-items-center gap-2 mb-3">
      <InputText
        value={mobileFilters.systemName}
        onChange={(e) => setMobileFilters(prev => ({ ...prev, systemName: e.target.value }))}
        placeholder="Search system..."
        className="flex-1"
      />
      <div className="relative">
        <Button
          icon="pi pi-filter"
          rounded
          outlined={activeFilterCount === 0}
          severity={activeFilterCount > 0 ? 'info' : undefined}
          onClick={openMobileFilterDialog}
        />
        {activeFilterCount > 0 && (
          <span
            className="absolute top-0 right-0 bg-primary text-white border-circle flex align-items-center justify-content-center"
            style={{ width: '1.2rem', height: '1.2rem', fontSize: '0.65rem', transform: 'translate(30%, -30%)' }}
          >
            {activeFilterCount}
          </span>
        )}
      </div>
    </div>
  );

  const mobileFilterDialogFooter = (
    <div className="flex justify-content-between">
      <Button label="Reset" icon="pi pi-times" text severity="secondary" onClick={resetMobileFilters} />
      <div className="flex gap-2">
        <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setMobileFilterDialog(false)} />
        <Button label="Apply" icon="pi pi-check" onClick={applyMobileFilters} />
      </div>
    </div>
  );

    const resetSensorErrorCountUi = (
      <>
        {resetRow && (
          <ResetSensorErrorCountsConfirmation
            title="Sensor error counter"
            message={<>The sensor error counter of device {resetRow.serial}{resetRow.nodeName ? ` (${resetRow.nodeName}${resetRow.systemName ? `, ${resetRow.systemName}` : ''})` : ''} will be set to 0.<br />Are you sure you want to reset it?</>}
            resetting={resetting}
            onCancel={() => setResetRow(null)}
            onReset={resetSensorErrorCount}
          />
        )}
        <Snackbar
          close
          closeNotification={() => setNotification(null)}
          color={notification?.color ?? 'success'}
          message={notification?.message ?? ''}
          open={!!notification}
          place='tc'
        />
      </>
    );

    const toolbar = (
        <div className="flex align-items-center justify-content-end gap-2 mb-3">
        {!mobileView && (
            <>
            <MultiSelect
            value={visibleColumns}
            options={columnOptions}
            onChange={(e) => setVisibleColumns(e.value)}
            placeholder="Columns"
            display="chip"
            className="w-20rem"
            />
            <Button label="Export" icon="pi pi-file-export" outlined onClick={() => exportDataTableCsv(dt, 'devices')} />
            </>
        )}
        </div>
    );

  if (mobileView) {
    return (
      <div data-testid="DevicesReactTable">
        {resetSensorErrorCountUi}
        {toolbar}
        {mobileFilterBar}
        <DataView value={mobileFilteredRows} itemTemplate={cardTemplate} paginator rows={10} />

        <Dialog
          visible={mobileFilterDialog}
          onHide={() => setMobileFilterDialog(false)}
          header="Filter &amp; Sort"
          style={{ width: '95vw' }}
          footer={mobileFilterDialogFooter}
          modal
          className="p-fluid"
        >
          <div className="mb-4">
            <label className="font-bold mb-2 block">Sort by</label>
            <div className="flex gap-2">
              <Dropdown
                value={pendingSortField}
                options={sortOptions}
                onChange={(e) => setPendingSortField(e.value)}
                className="flex-1"
              />
              <Button
                icon={pendingSortOrder === 1 ? 'pi pi-sort-amount-up' : 'pi pi-sort-amount-down'}
                outlined
                disabled={pendingSortField === 'default'}
                onClick={() => setPendingSortOrder(prev => (prev === 1 ? -1 : 1))}
                tooltip={pendingSortOrder === 1 ? 'Ascending' : 'Descending'}
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">System version</label>
            <Dropdown
              value={pendingFilters.systemVersionCategory}
              options={[
                { label: 'All', value: 'all' },
                { label: 'WSN', value: 'W2' },
                { label: 'LoRa', value: 'LoRa' },
              ]}
              onChange={(e) => setPendingFilters(prev => ({ ...prev, systemVersionCategory: e.value }))}
              placeholder="Any"
              className="w-full"
            />
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">Node</label>
            <InputText
              value={pendingFilters.nodeName}
              onChange={(e) => setPendingFilters(prev => ({ ...prev, nodeName: e.target.value }))}
              placeholder="Search..."
            />
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">Device EUI</label>
            <InputText
              value={pendingFilters.serial}
              onChange={(e) => setPendingFilters(prev => ({ ...prev, serial: e.target.value }))}
              placeholder="Search..."
            />
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">Device type</label>
            <InputText
              value={pendingFilters.type}
              onChange={(e) => setPendingFilters(prev => ({ ...prev, type: e.target.value }))}
              placeholder="Search..."
            />
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">Gen.</label>
            <Dropdown
              value={pendingFilters.gen}
              options={GEN_OPTIONS}
              onChange={(e) => setPendingFilters(prev => ({ ...prev, gen: e.value }))}
              placeholder="Any" showClear className="w-full"
            />
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">Firmware</label>
            <InputText
              value={pendingFilters.firmware}
              onChange={(e) => setPendingFilters(prev => ({ ...prev, firmware: e.target.value }))}
              placeholder="Search..."
            />
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">Gateway</label>
            <InputText
              value={pendingFilters.gatewayID}
              onChange={(e) => setPendingFilters(prev => ({ ...prev, gatewayID: e.target.value }))}
              placeholder="Search..."
            />
          </div>
        </Dialog>
      </div>
    );
  }

  return (
    <div data-testid="DevicesReactTable">
      {resetSensorErrorCountUi}
      {toolbar}

      <DataTable
        ref={dt}
        value={rows}
        exportFilename="devices"
        exportFunction={(e) => e.data instanceof Date ? e.data.toLocaleString('sv-SE').replace('T', ' ') : e.data}
        className="p-datatable-sm"
        stripedRows
        showGridlines
        scrollable
        paginator
        rows={25}
        rowsPerPageOptions={[25, 50, 100, 200, 500]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="{first} - {last} of {totalRecords}"
        resizableColumns
        columnResizeMode="expand"
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        filterDisplay="row"
        rowClassName={(row: DeviceRow) => ({
          'device-row-w2': row.systemVersion === 'W2',
          'device-row-lora': row.systemVersion !== 'W2',
        })}
      >
        {isVisible('systemName') && (
          <Column
            field="systemName"
            header="System"
            filter
            showFilterMenu={false}
            filterElement={(options) => <DebouncedTextFilter options={options} />}
            sortable
            body={(rowData) => (<div style={{paddingTop: '10px', paddingBottom: '10px'}}>
                <Link to={`/systems/${rowData.systemID}/dashboard`} className="text-primary">{rowData.systemName}</Link></div>
            )}
            style={{ width: 140 }} />
        )}
        <Column
          field="systemVersionCategory"
          header="Ver"
          filter showFilterMenu={false}
          filterElement={systemVersionFilterElement}
          style={{ width: 90 }}
          body={(row: DeviceRow) => renderVersionTag(row.systemVersion)}
        />
        {isVisible('nodeName') && (
          <Column field="nodeName" header="Node name" filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 130 }} />
        )}
        {isVisible('serial') && (
          <Column field="serial" header="Device EUI" filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 140 }} />
        )}
        {isVisible('subSerial') && (
          <Column field="subSerial" header="Sub serial" filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 140 }} />
        )}
        {isVisible('type') && (
          <Column
            field="type"
            header="Type"
            filter showFilterMenu={false}
            filterElement={(options) => <DebouncedTextFilter options={options} />}
            sortable
            style={{ width: 110 }}
            body={(row: DeviceRow) => renderDeviceTypeTag(row.type)}
          />
        )}
        {isVisible('gen') && (
          <Column
            field="gen"
            header="Gen."
            filter showFilterMenu={false}
            filterElement={(options) => (
              <Dropdown value={options.value} options={GEN_OPTIONS} onChange={(e) => options.filterApplyCallback(e.value)} placeholder="Any" showClear className="w-full" />
            )}
            sortable
            style={{ width: 70 }}
          />
        )}
        {isVisible('subType') && (
          <Column field="subType" header="Sub type" filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 110 }} />
        )}
        {isVisible('batteries') && (
          <Column field="batteries" header="Batteries" filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 130 }} />
        )}
        {isVisible('batteryPercentage') && (
          <Column
            field="batteryPercentage"
            header="Bat%"
            filter showFilterMenu={false}
            filterElement={(options) => <DebouncedTextFilter options={options} />}
            sortable
            style={{ width: 90 }}
            body={(row: DeviceRow) => renderBatteryPercentageTag(row.batteryPercentage, row.supplyState)}
          />
        )}
        {isVisible('rangefinderStatus') && (
          <Column
            field="rangefinderStatus"
            header="Sensor status"
            filter showFilterMenu={false}
            filterElement={(options) => <DebouncedTextFilter options={options} />}
            sortable
            style={{ width: 120 }}
            body={(row: DeviceRow) => renderSensorStatusTag(row.rangefinderStatus)}
          />
        )}
        {isVisible('sensorErrorCount') && (
          <Column
            field="sensorErrorCount"
            header="Sens. err. count"
            headerTooltip="How many times a sensor error appeared since the counter was last reset"
            filter showFilterMenu={false}
            filterElement={(options) => <DebouncedTextFilter options={options} />}
            sortable
            style={{ width: 120 }}
            body={sensorErrorCountCell}
          />
        )}
        {isVisible('temperature1') && (
          <Column
            field="temperature1"
            header="Temp."
            filter showFilterMenu={false}
            filterElement={(options) => <DebouncedTextFilter options={options} />}
            sortable
            style={{ width: 90 }}
            body={(row: DeviceRow) => (
              <div style={{ backgroundColor: row.temperatureOk === false ? '#fddede' : 'transparent', padding: '2px 4px' }}>
                {row.temperature1 != null ? `${row.temperature1}°C` : null}
              </div>
            )}
          />
        )}
        {isVisible('temperature2') && (
          <Column
            field="temperature2"
            header="Sens. Temp."
            filter showFilterMenu={false}
            filterElement={(options) => <DebouncedTextFilter options={options} />}
            sortable
            style={{ width: 90 }}
            body={(row: DeviceRow) => (row.temperature2 != null ? `${row.temperature2}°C` : null)}
          />
        )}
        {isVisible('supplyVoltage1') && (
          <Column
            field="supplyVoltage1"
            header="VCC"
            filter showFilterMenu={false}
            filterElement={(options) => <DebouncedTextFilter options={options} />}
            sortable
            style={{ width: 80 }}
            body={(row: DeviceRow) => (row.supplyVoltage1 != null ? `${row.supplyVoltage1}V` : null)}
          />
        )}
        {isVisible('supplyVoltage2') && (
          <Column
            field="supplyVoltage2"
            header="VDD"
            filter showFilterMenu={false}
            filterElement={(options) => <DebouncedTextFilter options={options} />}
            sortable
            style={{ width: 80 }}
            body={(row: DeviceRow) => (row.supplyVoltage2 != null ? `${row.supplyVoltage2}V` : null)}
          />
        )}
        {isVisible('firmware') && (
          <Column field="firmware" header="Firmware" filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 90 }} />
        )}
        {isVisible('node') && (
          <Column
            field="nodeLinked"
            header="In system"
            filter showFilterMenu={false}
            filterElement={nodeFilterElement}
            style={{ width: 90 }}
          />
        )}
        {isVisible('gatewayID') && (
          <Column field="gatewayID" header="Gateway ID" filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 130 }} />
        )}
        {isVisible('w2ConnectionStatus') && (
          <Column field="w2ConnectionStatus" header="WSN conn." filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 100 }} />
        )}
        {isVisible('lpt') && (
          <Column
            field="lpt"
            header="Latest packet"
            dataType="date"
            filter showFilterMenu={false}
            filterElement={lptFilterElement}
            sortable
            style={{ width: 260 }}
            body={(row: DeviceRow) => renderLptTag(row.lpt)}
          />
        )}
        {isVisible('snr') && (
          <Column field="snr" header="SNR" filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 70 }} />
        )}
        {isVisible('rssi') && (
          <Column field="rssi" header="RSSI" filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 70 }} />
        )}
        {isVisible('region') && (
          <Column field="region" header="Region" filter showFilterMenu={false} filterElement={(options) => <DebouncedTextFilter options={options} />} sortable style={{ width: 90 }} />
        )}

        <Column
          header="Details"
          style={{ width: 60 }}
          body={(row: DeviceRow) => (
            <Link to={`/devices/${row.id}/details`}>
              <Button
                icon="pi pi-eye"
                className="p-button-rounded p-button-outlined"
                aria-label="View device details"
              />
            </Link>
          )}
        />
      </DataTable>
    </div>
  );
};

const actions = { getDevices, resetSensorErrorCounts };
const mapStateToProps = (state: any) => ({
  mobileView: state.dashboard.mobileView,
  devices: state.dashboard.devices ?? [],
});

export default connect(mapStateToProps, actions)(DevicesList);